<template>
  <FormContainer>
    <template #title>Informations complémentaires</template>
    <template #content>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_138"
          :model-value="salariéEnCours?.natureContrat"
          @update:model-value="(value) => patchNatureContrat({ index: activeSalariéIndex!, value: value ?? null })"
          label="Nature du contrat"
          class="col-12 col-md-18 col-sm-36"
          :items="natureContrat"
          :required="metaSalarieEnCours?.natureContrat?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.natureContrat?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />

        <WrapperDropdown
          id="field_139"
          :model-value="salariéEnCours?.csp"
          @update:model-value="(value) => updateModule('csp', activeSalariéIndex!, value)"
          label="Catégorie socio-professionnelle (CSP)"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.Csp"
          :required="metaSalarieEnCours?.csp?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.csp?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
        <WrapperDropdown
          id="field_140"
          :model-value="salariéEnCours?.statut"
          @update:model-value="(value) => updateModule('statut', activeSalariéIndex!, value)"
          label="Statut salarié"
          class="col-12 col-md-18 col-sm-36"
          :items="statutItems"
          :required="metaSalarieEnCours?.statut?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.statut?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
      </div>
      <div class="form__bloc">
        <WrapperDropdown
          id="field_141"
          :model-value="salariéEnCours?.niveauFormation"
          @update:model-value="(value) => updateModule('niveauFormation', activeSalariéIndex!, value)"
          label="Niveau actuel de formation"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.NiveauDiplome"
          :required="metaSalarieEnCours?.niveauFormation?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.niveauFormation?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
        <WrapperInput
          v-if="metaSalarieEnCours?.numeroDossierCpf?.$status !== PropertyStatus.NotPresent"
          id="field_142"
          :model-value="salariéEnCours?.numeroDossierCpf"
          @update:model-value="(value) => updateModule('numeroDossierCpf', activeSalariéIndex!, value)"
          label="Numéro de dossier CPF"
          :required="metaSalarieEnCours?.numeroDossierCpf?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.numeroDossierCpf?.$messageErreurDirty!"
          hint="255 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :maxlength="255"
          deletable
          :readonly
        />

        <WrapperInput
          v-else
          id="field_142"
          label="Numéro de dossier CPF"
          hint="255 caractères maximum"
          class="col-12 col-md-18 col-sm-36"
          :readonly
          disabled
        />
        <WrapperDropdown
          v-if="metaSalarieEnCours?.eligibilite?.$status !== PropertyStatus.NotPresent"
          id="field_eligibilite"
          :model-value="salariéEnCours?.eligibilite"
          @update:model-value="(value) => updateModule('eligibilite', activeSalariéIndex!, value)"
          label="Eligibilité"
          class="col-12 col-md-18 col-sm-36"
          :items="lov.Eligibilite"
          :required="metaSalarieEnCours?.eligibilite?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.eligibilite?.$messageErreurDirty!"
          key-id="code"
          key-label="intitule"
          :readonly
        />
      </div>
      <div class="form__bloc">
        <BasicAlert
          v-if="salaireAlert"
          :alert-type="salaireAlert.type"
          :alert-label="salaireAlert.text"
          class="col-36"
        />
        <WrapperInputNumber
          id="field_143"
          :model-value="salariéEnCours?.tauxHoraireBrut"
          @update:model-value="(value) => updateModule('tauxHoraireBrut', activeSalariéIndex!, value)"
          label="Taux horaire brut"
          :required="metaSalarieEnCours?.tauxHoraireBrut?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.tauxHoraireBrut?.$messageErreurDirty!"
          class="col-6 col-md-12 col-sm-36"
          :decimal="2"
          deletable
          :readonly
        />
        <WrapperInputNumber
          id="field_144"
          :model-value="salariéEnCours?.pctTauxCharge"
          @update:model-value="(value) => updateModule('pctTauxCharge', activeSalariéIndex!, value)"
          label="Taux de charge (%)"
          :required="metaSalarieEnCours?.pctTauxCharge?.$status === PropertyStatus.Required"
          :alertLabel="metaSalarieEnCours?.pctTauxCharge?.$messageErreurDirty!"
          class="col-6 col-md-12 col-sm-36"
          pourcentage
          :decimal="2"
          deletable
          :readonly
        />
        <WrapperInputNumber
          id="field_145"
          :model-value="total"
          label="Total"
          class="col-6 col-md-12 col-sm-36"
          :decimal="2"
          disabled
          :readonly
        />
      </div>
    </template>
  </FormContainer>
</template>

<script setup lang="ts">
  import type { SalarieDTO } from '@/features/api/extranet/dto/Demande/AF'
  import { PropertyStatus } from '@/features/domaine/property-status/PropertyStatus'
  import { calculSalaire } from '@/features/domaine/regles-akto/shared'
  import { AF_SALARIES_ACTIONS } from '@/features/interface/demande/af/steps/salarie/useSalarieActions'
  import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore'
  import { FormContainer } from '@/features/interface/demande/shared'
  import { storeToRefs } from 'pinia'
  import { computed, inject } from 'vue'
  import type { AlertType } from '@designSystem/components'

  const store = useDemandeAFStore()
  const { setupArrayUpdaterWithKey } = store

  const { lov, readonly, model, estTT } = storeToRefs(store)
  const { activeSalariéIndex, salariéEnCours, metaSalarieEnCours, natureContrat, patchNatureContrat } = inject(AF_SALARIES_ACTIONS)!

  const updateModule = setupArrayUpdaterWithKey<SalarieDTO>('salaries')

  const idcc = computed(() => model.value?.employeur?.etablissement?.idcc?.code ?? '')

  const aCongésPayé = computed(() =>
    estTT.value &&
    model.value?.typePublic === 'Interimaire' &&
    salariéEnCours.value?.natureContrat === 'TravailTemporaire'
  )

  const total = computed(() => {
    const tauxHoraireBrut = salariéEnCours.value?.tauxHoraireBrut || 0
    const tauxCharge = salariéEnCours.value?.pctTauxCharge || 0

    const dureeFormation = model.value?.formation?.modules?.reduce((sum, mod) => sum + (mod.duree ?? 0), 0) ?? 0

    const multiplicateur = aCongésPayé.value ? 1.1 : 1

    return calculSalaire(tauxHoraireBrut, tauxCharge, dureeFormation) * multiplicateur
  })

  const statutItems = computed(() => {
    const statutItems3219 = ['Portage', 'Permanent', 'DemandeurEmploi']
    const items =
      idcc.value === '3219'
        ? lov.value.StatutSalarie.filter((x) => statutItems3219.includes(x.code))
        : lov.value.StatutSalarie
    return items
  })

  const salaireAlert = computed<{ text: string; type: AlertType } | undefined>(() => {
    if (metaSalarieEnCours.value?.tauxHoraireBrut?.$origine === 'max') {
      return {
        text: metaSalarieEnCours.value.tauxHoraireBrut.$messageErreur!,
        type: 'danger'
      }
    }
    if ((salariéEnCours.value?.tauxHoraireBrut ?? 0) > 50) {
      return {
        text: "Le taux horaire brut saisi est supérieur à 50 €. N'hésitez pas à apporter des corrections si nécessaire.",
        type: 'warning'
      }
    }
    if (aCongésPayé.value) {
      return {
        text: "Les 10% de congés payés sont compris dans le total.",
        type: 'info'
      }
    }
    return undefined
  })
</script>
